'use client';

import { ButtonHTMLAttributes, FC, MouseEvent, isValidElement } from 'react';
import { VariantProps, cva } from 'class-variance-authority';

import { QueryParams } from '@/typegen/sanity.types';
import { cn } from '@/lib/utils';
import { stegaCleanValue } from '@/utils/sanity/stegaCleanValue';
import { trackElementClick } from '@/utils/eventTracking';
import { useRouter } from 'next/navigation';

export const ctaButtonTypeVariants = cva(
	`w-full items-center justify-center rounded-full py-1 pl-3 pr-4 text-center uppercase hover:scale-[1.02] lg:py-1.5`,
	{
		variants: {
			variant: {
				primary:
					'bg-[var(--button-primary-background-default)] text-[var(--button-primary-text-default)] hover:bg-[var(--button-primary-split-cta-background-hover)] hover:text-[var(--button-primary-text-hover)] focus:bg-[var(--button-primary-background-hover)] focus:text-[var(--button-primary-text-hover)] active:bg-[var(--button-primary-background-active)] active:text-[var(--button-primary-text-active)] disabled:bg-[var(--button-primary-background-disabled)] disabled:text-[var(--button-primary-text-disabled)]',
				secondary:
					'bg-[var(--button-secondary-background-default)] text-[var(--button-secondary-text-default)] hover:bg-[var(--button-secondary-background-hover)] hover:text-[var(--button-secondary-text-hover)] focus:bg-[var(--button-secondary-background-hover)] focus:text-[var(--button-secondary-text-hover)] active:bg-[var(--button-secondary-background-active)] active:text-[var(--button-secondary-text-active)] disabled:bg-[var(--button-secondary-background-disabled)] disabled:text-[var(--button-secondary-text-disabled)]',
				tertiary:
					'bg-[var(--button-tertiary-background-default)] text-[var(--button-tertiary-text-default)] hover:bg-[var(--button-tertiary-background-hover)] hover:text-[var(--button-tertiary-text-hover)] focus:bg-[var(--button-tertiary-background-hover)] focus:text-[var(--button-tertiary-text-hover)] active:bg-[var(--button-tertiary-background-active)] active:text-[var(--button-tertiary-text-active)] disabled:bg-[var(--button-tertiary-background-disabled)] disabled:text-[var(--button-tertiary-text-disabled)]',
				text: 'bg-[var(--button-text-background-default)] text-[var(--button-text-text-default)] hover:bg-[var(--button-text-background-hover)] hover:text-[var(--button-text-text-hover)] focus:bg-[var(--button-text-background-hover)] focus:text-[var(--button-text-text-hover)] active:bg-[var(--button-text-background-active)] active:text-[var(--button-text-text-active)] disabled:bg-[var(--button-text-background-disabled)] disabled:text-[var(--button-text-text-disabled)]',
			},
		},
		defaultVariants: {
			variant: 'primary',
		},
	}
);

export interface CtaButtonTypeProps
	extends ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof ctaButtonTypeVariants> {
	actionLinkUrl?: string;
	pageQueryParams?:
		| ({
				_key: string;
		  } & QueryParams)[]
		| null;
	// Analytics tracking props
	location?: string;
	action?: string;
	linkType?: string;
	trackingText?: string;
}

export const CtaButtonType: FC<CtaButtonTypeProps> = ({
	variant,
	className,
	children,
	onClick,
	actionLinkUrl,
	pageQueryParams,
	// Analytics tracking props
	location = 'unspecified',
	action = 'click',
	linkType = actionLinkUrl?.startsWith('http') ? 'external' : 'internal',
	trackingText = null,
	...props
}) => {
	const router = useRouter();

	const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
		// Extract button text from children, handling StegaClean components
		let buttonText = 'Button';
		if (typeof children === 'string') {
			buttonText = children;
		} else if (
			isValidElement(children) &&
			children.props !== null &&
			typeof children.props === 'object' &&
			'value' in children.props &&
			typeof children.props.value === 'string'
		) {
			// Handle StegaClean components which have a value prop
			buttonText = children.props.value || 'Button';
		}

		// Track the click event
		trackElementClick({
			type: 'button',
			action,
			location,
			text: trackingText ? trackingText : buttonText,
			linkType,
			pathname: window.location.pathname,
			domain: window.location.hostname,
		});

		if (onClick) {
			onClick?.(e);
		}
		if (actionLinkUrl) {
			if (linkType === 'external') {
				window.open(actionLinkUrl, '_blank');
				return;
			}

			if (actionLinkUrl !== '#' && actionLinkUrl.charAt(0) !== '/') {
				actionLinkUrl = `/${actionLinkUrl}`;
			}

			if (pageQueryParams && pageQueryParams.length > 0) {
				const params = new URLSearchParams(
					pageQueryParams?.map((pair) => [pair.key || '', pair.value || '']) || {}
				);
				router.push(`${actionLinkUrl}?${params.toString()}`);
			} else {
				router.push(actionLinkUrl);
			}
		}
	};

	return (
		<button
			className={cn(
				ctaButtonTypeVariants({
					className,
					variant: stegaCleanValue(variant) as CtaButtonTypeProps['variant'],
				})
			)}
			{...props}
			onClick={handleClick}
		>
			{children}
		</button>
	);
};
